import { z } from 'zod';

/**
 * Basis-URL des Cloudflare R2-Medien-Buckets.
 * Wird für alle statischen Assets (Team-Logos, Event-Fotos etc.) verwendet.
 */
export const MEDIA_BASE_URL = 'https://media.fanini.live';

/**
 * Upload-Kategorien für MediaFiles.
 * Bestimmt erlaubte MIME-Types, Größenbeschränkungen und Standard-Sichtbarkeit.
 *
 * Bestehende Kategorien:
 * - event-photo — Event-Fotos (JPEG, PNG, WebP)
 * - event-document — Event-Dokumente (PDF, Bilder)
 * - ticket-pdf — Ticket-PDFs (nicht öffentlich)
 * - website-asset — Website-Assets inkl. SVG
 * - info-document — Info-Dokumente (PDF)
 *
 * Brand-Kategorien (DAM):
 * - brand-logo — Logos (Faninitiative, Eintracht Spandau)
 * - brand-icon — Icons
 * - brand-brush-stroke — Pinselstriche
 * - brand-ripped-paper — Ripped-Paper-Elemente
 * - brand-background — Hintergrundgrafiken
 * - brand-misc — Sonstiges Brand-Material
 */
export const MediaCategorySchema = z.enum([
  'event-photo',
  'event-document',
  'ticket-pdf',
  'website-asset',
  'info-document',
  'brand-logo',
  'brand-icon',
  'brand-brush-stroke',
  'brand-ripped-paper',
  'brand-background',
  'brand-misc',
]);
/** Upload-Kategorie als TypeScript-Type. */
export type MediaCategory = z.infer<typeof MediaCategorySchema>;

/**
 * Vollständiges MediaFile Zod Schema — entspricht dem Datenbankmodell.
 * Wird für API-Responses und Type-Inference verwendet.
 */
export const MediaFileSchema = z.object({
  id: z.string(),
  storageKey: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  size: z.number().int().positive(),
  category: MediaCategorySchema,
  /** Optionaler Unterordner — primär für Brand-Assets (z.B. 'svg', 'png', 'faninitiative'). */
  folder: z.string().nullable().optional(),
  publicUrl: z.string().nullable(),
  eventId: z.string().nullable().optional(),
  uploadedById: z.string(),
  isPublic: z.boolean().default(true),
  altText: z.string().nullable().optional(),
  caption: z.string().nullable().optional(),
  sortOrder: z.number().int().default(0),
  deletedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
/** MediaFile TypeScript-Type — abgeleitet aus dem Zod Schema. */
export type MediaFile = z.infer<typeof MediaFileSchema>;

/**
 * Schema für Upload-Metadaten.
 * Wird zusammen mit der Datei im Multipart-Request mitgeschickt.
 */
export const CreateMediaFileSchema = z.object({
  category: MediaCategorySchema,
  /** Optionaler Unterordner für Brand-Assets (z.B. 'svg', 'png', 'faninitiative'). */
  folder: z.string().max(200).optional(),
  eventId: z.string().optional(),
  altText: z.string().max(500).optional(),
  caption: z.string().max(1000).optional(),
  isPublic: z.boolean().optional(),
});
/** CreateMediaFile TypeScript-Type — Eingabe beim Upload. */
export type CreateMediaFile = z.infer<typeof CreateMediaFileSchema>;

/**
 * Schema für Metadaten-Update eines bestehenden MediaFiles.
 * Alle Felder optional — nur übergebene Felder werden aktualisiert.
 */
export const UpdateMediaFileSchema = z.object({
  altText: z.string().max(500).nullable().optional(),
  caption: z.string().max(1000).nullable().optional(),
  sortOrder: z.number().int().optional(),
  eventId: z.string().nullable().optional(),
  isPublic: z.boolean().optional(),
  /** Optionaler Unterordner — null setzt den Ordner zurück. */
  folder: z.string().max(200).nullable().optional(),
});
/** UpdateMediaFile TypeScript-Type — Eingabe beim Metadaten-Update. */
export type UpdateMediaFile = z.infer<typeof UpdateMediaFileSchema>;

/**
 * Schema für Galerie-Sortierung.
 * Erlaubt das Neu-Sortieren mehrerer Dateien in einem einzelnen Request.
 */
export const MediaReorderSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      sortOrder: z.number().int(),
    }),
  ),
});
/** MediaReorder TypeScript-Type — Eingabe beim Neu-Sortieren. */
export type MediaReorder = z.infer<typeof MediaReorderSchema>;

/** Erlaubte MIME-Types für alle Brand-Kategorien (SVG + PNG + JPEG + WebP). */
const BRAND_MIME_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'] as const;

/**
 * Erlaubte MIME-Types pro Upload-Kategorie.
 * Wird zur Validierung vor dem Upload auf Client und Server verwendet.
 * Brand-Kategorien erlauben zusätzlich SVG (`image/svg+xml`).
 */
export const ALLOWED_MIME_TYPES: Record<MediaCategory, string[]> = {
  'event-photo': ['image/jpeg', 'image/png', 'image/webp'],
  'event-document': ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
  'ticket-pdf': ['application/pdf'],
  'website-asset': ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  'info-document': ['application/pdf'],
  'brand-logo': [...BRAND_MIME_TYPES],
  'brand-icon': [...BRAND_MIME_TYPES],
  'brand-brush-stroke': [...BRAND_MIME_TYPES],
  'brand-ripped-paper': [...BRAND_MIME_TYPES],
  'brand-background': ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  'brand-misc': [...BRAND_MIME_TYPES],
};

/**
 * Maximale Dateigröße in Bytes pro Upload-Kategorie.
 * Wird zur Validierung vor dem Upload auf Client und Server verwendet.
 * Brand-Kategorien erlauben bis zu 20 MB (SVG-Dateien können größer sein).
 */
export const MAX_FILE_SIZE: Record<MediaCategory, number> = {
  'event-photo': 10 * 1024 * 1024,
  'event-document': 10 * 1024 * 1024,
  'ticket-pdf': 5 * 1024 * 1024,
  'website-asset': 10 * 1024 * 1024,
  'info-document': 10 * 1024 * 1024,
  'brand-logo': 20 * 1024 * 1024,
  'brand-icon': 20 * 1024 * 1024,
  'brand-brush-stroke': 20 * 1024 * 1024,
  'brand-ripped-paper': 20 * 1024 * 1024,
  'brand-background': 20 * 1024 * 1024,
  'brand-misc': 20 * 1024 * 1024,
};

/**
 * Standard-Sichtbarkeit (`isPublic`) pro Upload-Kategorie.
 * Ticket-PDFs sind standardmäßig nicht öffentlich.
 * Alle Brand-Assets sind standardmäßig öffentlich (für CDN-Auslieferung).
 */
export const DEFAULT_PUBLIC: Record<MediaCategory, boolean> = {
  'event-photo': true,
  'event-document': true,
  'ticket-pdf': false,
  'website-asset': true,
  'info-document': true,
  'brand-logo': true,
  'brand-icon': true,
  'brand-brush-stroke': true,
  'brand-ripped-paper': true,
  'brand-background': true,
  'brand-misc': true,
};

/**
 * Bild-Varianten die beim Upload generiert werden.
 * Thumbnails für Listen, Medium für Detail-Ansichten, Large für Lightbox.
 */
export const IMAGE_VARIANTS = {
  thumbnail: { width: 200, quality: 70, suffix: '_thumb' },
  medium: { width: 800, quality: 80, suffix: '_medium' },
  large: { width: 1600, quality: 85, suffix: '_large' },
} as const;

/** Schlüssel der verfügbaren Bild-Varianten. */
export type ImageVariant = keyof typeof IMAGE_VARIANTS;
